import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'

interface Automation {
  enabled: boolean
  si_receipt: boolean
  bl_forward: boolean
  quote_reply: boolean
  require_verified_client: boolean
  test_mode_enabled: boolean
  test_mode_override_email: string | null
}

interface Props {
  automation: Automation
}

function ToggleRow({
  id,
  checked,
  onChange,
  label,
  description,
}: {
  id: string
  checked: boolean
  onChange: (value: boolean) => void
  label: string
  description: string
}) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
        className="mt-0.5 cursor-pointer"
      />
      <div>
        <Label htmlFor={id} className="cursor-pointer text-[13px] font-medium">
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}

export default function SettingsAutomation({ automation }: Props) {
  const pathFor = useOrgPath()

  const { data, setData, patch, processing } = useForm({
    enabled: automation.enabled,
    si_receipt: automation.si_receipt,
    bl_forward: automation.bl_forward,
    quote_reply: automation.quote_reply,
    require_verified_client: automation.require_verified_client,
    test_mode_enabled: automation.test_mode_enabled,
    test_mode_override_email: automation.test_mode_override_email || '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(pathFor('/settings/automation'))
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Settings</h1>
        <Button type="submit" form="automation-form" size="sm" className="text-xs" disabled={processing}>
          Save Changes
        </Button>
      </header>

      <div className="max-w-2xl overflow-y-auto p-8">
        <form id="automation-form" onSubmit={handleSubmit} className="space-y-8">
          <section>
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Auto-send
            </h2>
            <p className="mb-4 text-xs text-muted-foreground">
              By default the agent drafts every reply for a human to review and send. Turn this on to let it
              send routine, fully-checked SI/BL acknowledgements on its own. Each message still has to pass
              the safety checks first, and you can switch this off any time. Only account admins can change
              these settings.
            </p>

            <div className="space-y-4">
              <ToggleRow
                id="auto_send_enabled"
                checked={data.enabled}
                onChange={(v) => setData('enabled', v)}
                label="Enable auto-send"
                description="Master switch. Off = everything stays a draft for review (current behavior)."
              />

              <div className={data.enabled ? 'space-y-4 border-l pl-4' : 'space-y-4 border-l pl-4 opacity-50 pointer-events-none'}>
                <ToggleRow
                  id="auto_send_si_receipt"
                  checked={data.si_receipt}
                  onChange={(v) => setData('si_receipt', v)}
                  label="Auto-send SI receipts"
                  description="Acknowledge a Shipping Instructions submission once it passes all checks."
                />
                <ToggleRow
                  id="auto_send_bl_forward"
                  checked={data.bl_forward}
                  onChange={(v) => setData('bl_forward', v)}
                  label="Auto-forward clean BL drafts"
                  description="Forward a BL draft that reconciles with the SI to the client for confirmation."
                />
                <ToggleRow
                  id="auto_send_quote_reply"
                  checked={data.quote_reply}
                  onChange={(v) => setData('quote_reply', v)}
                  label="Auto-send quote replies"
                  description="Send the agent's rate quote to the client as soon as live rates are found. Clarification questions and no-rate replies stay drafts."
                />
                <ToggleRow
                  id="auto_send_require_verified_client"
                  checked={data.require_verified_client}
                  onChange={(v) => setData('require_verified_client', v)}
                  label="Only auto-send to verified clients"
                  description="Never auto-send to a prospect (unverified) client. Recommended on."
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Outbound email test mode
            </h2>
            <p className="mb-4 text-xs text-muted-foreground">
              While this is on, every email Nereus sends — agent drafts you approve, auto-sends, replies you
              write — goes to the override address below instead of the real recipient. The subject is
              prefixed with the original recipient so you can tell what would have gone where. Nothing
              reaches customers.
            </p>
            <div className="space-y-4">
              <ToggleRow
                id="test_mode_enabled"
                checked={data.test_mode_enabled}
                onChange={(v) => setData('test_mode_enabled', v)}
                label="Redirect all outbound email"
                description="A banner shows on every page while this is on."
              />
              <div className="border-l pl-4">
                <Label htmlFor="test_mode_override_email" className="text-[13px] font-medium">Send everything to</Label>
                <Input
                  id="test_mode_override_email"
                  type="email"
                  placeholder="you@xport.com"
                  className="mt-1 h-9 max-w-sm text-[13px]"
                  value={data.test_mode_override_email}
                  onChange={(e) => setData('test_mode_override_email', e.target.value)}
                  required={data.test_mode_enabled}
                />
              </div>
            </div>
          </section>
        </form>
      </div>
    </>
  )
}

SettingsAutomation.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
